/** Screen scaffolding — design spec §2 and §3. */

import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/cn";

/** §3 — 22/700 with -.2px tracking, optionally over a 13px `--hint` eyebrow. */
export function ScreenTitle({
  children,
  eyebrow,
  trailing,
}: {
  children: ReactNode;
  eyebrow?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        {eyebrow != null && <p className="mb-0.5 text-meta text-hint">{eyebrow}</p>}
        <h1 className="text-screen font-bold text-text">{children}</h1>
      </div>
      {trailing != null && <div className="shrink-0 pb-1">{trailing}</div>}
    </div>
  );
}

/** §3 — 15–16/700, with an optional counter or action on the right. */
export function SectionTitle({
  children,
  trailing,
  className,
}: {
  children: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-between gap-2", className)}>
      <h2 className="text-section font-bold text-text">{children}</h2>
      {trailing != null && <div className="shrink-0">{trailing}</div>}
    </div>
  );
}

/**
 * §3 — the uppercase service label, reserved for group headings (project
 * areas, the component-sheet headings) and nothing else.
 */
export function GroupLabel({
  children,
  icon,
  className,
}: {
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      {icon}
      {/* DS caption: 12/16 600, +0.08em. */}
      <span className="text-micro font-semibold uppercase tracking-[.08em] text-p-text-muted">{children}</span>
    </div>
  );
}

/** §2 — sections are 16 apart (12 on dense screens); content within them, 8. */
export function Section({
  title,
  trailing,
  children,
  className,
}: {
  title?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-2", className)}>
      {title != null && <SectionTitle trailing={trailing}>{title}</SectionTitle>}
      {children}
    </section>
  );
}

/**
 * §8 — a collapsible section ("Похожее из памяти"). The whole header row is
 * the target and clears 44px, not just the chevron.
 */
export function CollapsibleSection({
  title,
  icon,
  count,
  defaultOpen = false,
  children,
}: {
  title: ReactNode;
  icon?: ReactNode;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="press-surface flex min-h-cell w-full items-center gap-2 text-left"
      >
        {icon}
        <h2 className="text-section font-bold text-text">{title}</h2>
        {count != null && <span className="text-section text-hint">{count}</span>}
        <span className="flex-1" />
        {open ? (
          <ChevronUp className="h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-[18px] w-[18px] shrink-0 text-hint" aria-hidden="true" />
        )}
      </button>
      {open && <div className="flex flex-col gap-2">{children}</div>}
    </section>
  );
}
